import { AdminNavbarGroup } from "@/lib/Components/Common/AdminNavbarGroup";
import type { RecordScreenLink } from "@/lib/Components/Screens/RecordScreen/RecordScreen";
import {
  Navbar,
  NavbarItemLink,
  NavbarSection,
} from "@/components/catalyst/navbar";
import { useMatchRoute } from "@tanstack/react-router";
import { Badge } from "@/components/catalyst/badge";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";

interface RecordScreenContentTabsProps {
  tabs: RecordScreenLink[];
  params?: Record<string, string>;
}

export function RecordScreenContentTabs({
  tabs,
  params,
}: RecordScreenContentTabsProps) {
  "use no memo";

  const matchRoute = useMatchRoute();
  const { isAdmin } = useTenant();

  const visibleTabs = tabs.filter((t) => !t.isHidden);
  const regularTabs = visibleTabs.filter((t) => !t.isAdminTab);
  const adminTabs = isAdmin ? visibleTabs.filter((t) => t.isAdminTab) : [];

  function renderTab(tab: RecordScreenLink) {
    return (
      <NavbarItemLink
        key={tab.to}
        to={tab.to}
        params={(p) => ({ ...p, ...params })}
        search={tab.search}
        current={
          !!matchRoute({
            to: tab.to,
            fuzzy: tab.fuzzy,
          })
        }
        activeOptions={{
          exact: true,
        }}
        viewTransition
      >
        <tab.Icon aria-hidden="true" className="size-4" data-slot="icon" />
        <span className="whitespace-nowrap">{tab.name}</span>

        {tab.notificationCount && tab.notificationCount > 0 ? (
          <span className="inline shrink-0 rounded-md bg-indigo-500 px-2 text-white">
            {tab.notificationCount}
          </span>
        ) : null}

        {tab.isBeta ? <Badge color="purple">beta</Badge> : null}
      </NavbarItemLink>
    );
  }

  return (
    <Navbar className="overflow-x-auto border-b border-gray-200">
      <NavbarSection>
        {regularTabs.map(renderTab)}

        {adminTabs.length > 0 ? (
          <AdminNavbarGroup>{adminTabs.map(renderTab)}</AdminNavbarGroup>
        ) : null}
      </NavbarSection>
    </Navbar>
  );
}
